import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormikContext, FormikValues, Field } from 'formik';
import { ToggleField, UserSelectField, Select } from '../../../components';
import type { Value } from '../../../components';
import {
    RootState, useAppDispatch, useAppSelector, loadProjects, loadBoards,
} from '../../../redux';
import { Board, Project, User } from '../../../models';

const fieldClassName =
    'block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6';

const selectClassName = '!mt-0 !w-full border border-gray-300 dark:border-gray-600 rounded-lg';
const selectContainerClassName =
    'relative w-full cursor-default rounded-lg bg-white dark:bg-gray-800 py-2 pl-3 pr-10 text-left focus:outline-none sm:text-sm shadow-sm';

function MileageForm() {
    const { t, i18n } = useTranslation();
    const dispatch = useAppDispatch();
    const { projects } = useAppSelector((state: RootState) => state.projects);
    const { boards } = useAppSelector((state: RootState) => state.boards);
    const { values, setFieldValue } = useFormikContext<FormikValues>();

    const mileage = (values.mileage || {}) as Record<string, unknown>;
    const projectId = String(mileage.project || '');
    const boardId = String(mileage.board || '');
    const columnId = String(mileage.column || '');

    const selectedProject = useMemo(
        () => (projects || []).find((p: Project) => p._id === projectId),
        [projects, projectId]
    );

    const isListLanding = selectedProject?.posture?.defaultLanding === 'list';
    const canPlaceOnBoard = !!projectId && !isListLanding;

    useEffect(() => {
        dispatch(loadProjects());
    }, [dispatch]);

    useEffect(() => {
        if (canPlaceOnBoard)
            dispatch(loadBoards({ project: projectId }));
    }, [dispatch, canPlaceOnBoard, projectId]);

    useEffect(() => {
        if (!canPlaceOnBoard) {
            if (mileage.board)
                setFieldValue('mileage.board', null);
            if (mileage.column)
                setFieldValue('mileage.column', null);
        }
    }, [canPlaceOnBoard, mileage.board, mileage.column, setFieldValue]);

    const projectOptions = useMemo(() => {
        const list = (projects || []).slice().sort((a: Project, b: Project) => a.name.localeCompare(b.name));
        const extra = projectId && !list.some((p: Project) => p._id === projectId)
            ? [{ value: projectId, label: projectId }]
            : [];
        return [
            { value: '', label: t('settings.mileage_not_set') },
            ...extra,
            ...list.map((p: Project) => ({ value: p._id, label: p.name })),
        ];
    }, [projects, t, projectId]);

    const localized = (value: unknown) => {
        if (value == null)
            return '';
        if (typeof value === 'string')
            return value;
        const map = value as Record<string, string>;
        return map[i18n.language] || map.fr || map.en || map.de || '';
    };

    const boardOptions = useMemo(() => {
        const list = (boards || [])
            .filter((b: Board) => (b.columns || []).some((column) => !column.isSmart))
            .map((b: Board) => ({
                value: b._id,
                label: localized(b.name) || b._id,
            }));
        return [
            { value: '', label: t('settings.mileage_not_set') },
            ...list,
        ];
    }, [boards, t, i18n.language]);

    const selectedBoard = useMemo(
        () => (boards || []).find((b: Board) => b._id === boardId),
        [boards, boardId]
    );

    const columnOptions = useMemo(() => {
        const list = ((selectedBoard?.columns || []) as AnyObj[])
            .filter((c) => !c.isSmart)
            .map((c) => ({
                value: c._id,
                label: localized(c.title) || c._id,
            }));
        return [
            { value: '', label: t('settings.mileage_not_set') },
            ...list,
        ];
    }, [selectedBoard, t, i18n.language]);

    const onProjectChange = (value?: Value) => {
        const nextId = value ? String(value) : '';
        setFieldValue('mileage.project', nextId || null);
        setFieldValue('mileage.board', null);
        setFieldValue('mileage.column', null);
    };

    const onBoardChange = (value?: Value) => {
        setFieldValue('mileage.board', value ? String(value) : null);
        setFieldValue('mileage.column', null);
    };

    return (
        <div className="flex flex-col lg:flex-row space-y-6 lg:space-y-0 space-x-0 lg:space-x-12">
            <div className="flex-1">
                <h2 className="mt-4 mb-1 text-base font-semibold leadin text-lg text-gray-900 dark:text-gray-100">
                    {t('settings.mileage')}
                </h2>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    {t('settings.mileage_help')}
                </p>

                <div className="mt-5">
                    <div className="mt-2 flex items-center justify-between gap-3">
                        <label className="text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                            {t('settings.enable_mileage')}
                        </label>
                        <ToggleField name="mileage.enabled" className="inline" />
                    </div>
                </div>

                {/* 1 + 2 : taux / devise */}
                <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                            {t('settings.mileage_rate_per_km')}
                        </label>
                        <div className="mt-2">
                            <Field
                                type="number"
                                step="0.01"
                                min="0"
                                name="mileage.rate_per_km"
                                className={fieldClassName}
                            />
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                            {t('settings.mileage_currency')}
                        </label>
                        <div className="mt-2">
                            <Field
                                type="text"
                                name="mileage.currency"
                                className={fieldClassName}
                                placeholder="CHF"
                            />
                        </div>
                    </div>
                </div>

                {/* 3 + 4 : jour / fuseau */}
                <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                            {t('settings.mileage_generation_day')}
                        </label>
                        <div className="mt-2">
                            <Field
                                type="number"
                                min="1"
                                max="28"
                                name="mileage.generation_day"
                                className={fieldClassName}
                            />
                        </div>
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            {t('settings.mileage_generation_day_help')}
                        </p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                            {t('settings.mileage_timezone')}
                        </label>
                        <div className="mt-2">
                            <Field
                                type="text"
                                name="mileage.timezone"
                                className={fieldClassName}
                                placeholder="Europe/Zurich"
                            />
                        </div>
                    </div>
                </div>

                {/* 5 + 6 + 7 : projet / board / colonne */}
                <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                            {t('settings.mileage_project')}
                        </label>
                        <div className="mt-2">
                            <Select
                                key={`project-${projectId || 'none'}`}
                                defaultValue={projectId}
                                options={projectOptions}
                                placeholder={t('settings.mileage_not_set') || ''}
                                onChange={onProjectChange}
                                className={selectClassName}
                                containerClassName={selectContainerClassName}
                                selectedClassName="h-[26px] font-medium flex items-center"
                                inline
                            />
                        </div>
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            {t('settings.mileage_project_help')}
                        </p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                            {t('settings.mileage_board')}
                        </label>
                        <div className="mt-2">
                            {canPlaceOnBoard ? (
                                <Select
                                    key={`board-${projectId}-${boardId || 'none'}`}
                                    defaultValue={boardId}
                                    options={boardOptions}
                                    placeholder={t('settings.mileage_not_set') || ''}
                                    onChange={onBoardChange}
                                    className={selectClassName}
                                    containerClassName={selectContainerClassName}
                                    selectedClassName="h-[26px] font-medium flex items-center"
                                    inline
                                />
                            ) : (
                                <p className="rounded-md border border-dashed border-gray-300 dark:border-gray-600 px-3 py-2 text-xs text-gray-500 dark:text-gray-400">
                                    {projectId
                                        ? t('settings.mileage_board_list_landing')
                                        : t('settings.mileage_board_needs_project')}
                                </p>
                            )}
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                            {t('settings.mileage_column')}
                        </label>
                        <div className="mt-2">
                            {canPlaceOnBoard && boardId ? (
                                <Select
                                    key={`column-${boardId}-${columnId || 'none'}`}
                                    defaultValue={columnId}
                                    options={columnOptions}
                                    placeholder={t('settings.mileage_not_set') || ''}
                                    onChange={(value?: Value) => {
                                        setFieldValue('mileage.column', value ? String(value) : null);
                                    }}
                                    className={selectClassName}
                                    containerClassName={selectContainerClassName}
                                    selectedClassName="h-[26px] font-medium flex items-center"
                                    inline
                                />
                            ) : (
                                <p className="rounded-md border border-dashed border-gray-300 dark:border-gray-600 px-3 py-2 text-xs text-gray-500 dark:text-gray-400">
                                    {!canPlaceOnBoard
                                        ? (projectId
                                            ? t('settings.mileage_board_list_landing')
                                            : t('settings.mileage_board_needs_project'))
                                        : t('settings.mileage_column_needs_board')}
                                </p>
                            )}
                        </div>
                    </div>
                </div>

                {/* 8 + 9 : assignés / modèle de titre */}
                <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                            {t('settings.mileage_assignees')}
                        </label>
                        <div className="mt-2">
                            <UserSelectField
                                name="mileage.assignees"
                                multiple
                                roles={[User.ROLE_ADMIN, User.ROLE_USER]}
                                placeholder={t('settings.mileage_assignees_placeholder') || ''}
                            />
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                            {t('settings.mileage_card_title_template')}
                        </label>
                        <div className="mt-2">
                            <Field
                                type="text"
                                name="mileage.card_title_template"
                                className={fieldClassName}
                            />
                        </div>
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            {t('settings.mileage_card_title_template_help')}
                        </p>
                    </div>
                </div>

                <div className="mt-5">
                    <div className="mt-2 flex items-center justify-between gap-3">
                        <label className="text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                            {t('settings.mileage_include_detail')}
                        </label>
                        <ToggleField name="mileage.include_detail_in_description" className="inline" />
                    </div>
                </div>
            </div>
        </div>
    );
}

export default MileageForm;
